import type { FC, ReactNode } from 'react'

import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { EASE_OUT_EXPO, MaskLines, Reveal } from '@/components/motion/reveal'
import { CountUp } from '@/components/motion/roll-text'
import { ToneSection } from '@/components/generals/tone'
import { Cta } from '@/components/generals/cta'
import { IndustryHero } from '@/components/landing/industry/industry-hero'
import { ResourceDownload } from '@/components/landing/industry/resource-download'
import { Testimonials } from '@/components/landing/shared/testimonials'
import { MustHaves } from '@/components/landing/shared/must-haves'
import { clients, testimonials } from '@/constants/content'
import { resources } from '@/constants/resources'

const gamingClients = [clients.eneba, clients.eldorado, clients.zeusx, clients.driffle, clients.bigkid]
const gamingVoices = testimonials.filter(item => ['Eneba', 'Eldorado'].includes(item.company))

const Hud: FC = (): ReactNode => (
    <div aria-hidden className='relative w-full max-w-xs p-5 font-mono text-[0.6875rem] uppercase tracking-[0.12em]'>
        {['left-0 top-0 border-l border-t', 'right-0 top-0 border-r border-t', 'bottom-0 left-0 border-b border-l', 'bottom-0 right-0 border-b border-r'].map(corner => (
            <span key={corner} className={cn('absolute size-4 border-tone-accent', corner)} />
        ))}
        <p className='text-tone-accent'>
            Adaptive AI · Online
        </p>
        <div className='mt-5 flex flex-col gap-4'>
            {[
                { label: 'Approval', value: '98%', fill: 49 },
                { label: 'Chargeback liability', value: '100%', fill: 50 },
                { label: 'Decision', value: '<500ms', fill: 42 }
            ].map(stat => (
                <div key={stat.label} className='flex flex-col gap-2'>
                    <span className='flex justify-between text-tone-muted'>
                        <span>
                            {stat.label}
                        </span>
                        <span className='text-tone'>
                            {stat.value}
                        </span>
                    </span>
                    <span className='grid grid-cols-[repeat(25,1fr)] gap-[2px]'>
                        {Array.from({ length: 25 }, (_, index) => (
                            <span key={index} className={cn('h-2', index < Math.round(stat.fill / 2) ? 'bg-tone-accent' : 'bg-tone/10')} />
                        ))}
                    </span>
                </div>
            ))}
        </div>
    </div>
)

const SCORES = [
    { value: 20, label: 'Declined by existing payment fraud solutions in the gaming industry.', tone: 'bg-tone' },
    { value: 87, label: 'Of declined transactions in the gaming industry are false positives.', tone: 'bg-mint' },
    { value: 65, label: 'Of declined transactions are attributed to new users.', tone: 'bg-violet' },
    { value: 2, label: 'Of declined transactions are proven to be fraudulent.', tone: 'bg-ember' }
]

const ScoreRow: FC<{ value: number, label: string, tone: string, index: number }> = ({ value, label, tone, index }): ReactNode => {
    const filled = Math.max(1, Math.round(value / 2))

    return (
        <li className='grid gap-5 border-t border-tone-line py-8 md:grid-cols-12 md:items-center md:gap-8'>
            <span className='t-label text-tone-muted md:col-span-1'>
                0{index + 1}
            </span>
            <span className='font-heading text-[clamp(4rem,8vw,7.5rem)] font-semibold leading-[0.82] tracking-[-0.05em] wdth-62 md:col-span-2'>
                <CountUp to={value} suffix='%' />
            </span>
            <div className='flex flex-col gap-4 md:col-span-9'>
                <p className='max-w-xl text-lg leading-snug'>
                    {label}
                </p>
                <motion.div
                    aria-hidden
                    className='grid grid-cols-[repeat(50,1fr)] gap-[3px]'
                    initial='hidden'
                    whileInView='visible'
                    viewport={{ once: true, margin: '0px 0px -15% 0px' }}
                >
                    {Array.from({ length: 50 }, (_, segment) => (
                        <motion.span
                            key={segment}
                            className={cn('h-3 md:h-4', segment < filled ? tone : 'bg-tone/10')}
                            variants={{
                                hidden: { opacity: segment < filled ? 0 : 1, scaleY: segment < filled ? 0.2 : 1 },
                                visible: { opacity: 1, scaleY: 1, transition: { duration: 0.4, ease: EASE_OUT_EXPO, delay: segment * 0.018 } }
                            }}
                        />
                    ))}
                </motion.div>
            </div>
        </li>
    )
}

export const GamingIndustry: FC = (): ReactNode => {
    return (
        <>
            <IndustryHero
                tone='gaming'
                accent='#35d5ba'
                index='02'
                name='Gaming'
                word='Gaming'
                lines={[
                    'Don’t let caution',
                    'keep you out',
                    <span key='game'>
                        of the <span className='font-serif font-normal normal-case italic tracking-normal text-tone-accent wdth-100'>game</span>
                    </span>
                ]}
                intro='AI gave fraudsters the cheat codes. It’s time to level the playing field. Enter Adaptive AI.'
                clients={gamingClients}
                visual={<Hud />}
            />

            <ToneSection tone='paper' id='scoreboard' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
                <div className='mb-16 grid gap-8 lg:mb-24 lg:grid-cols-12'>
                    <span className='t-label text-tone-muted lg:col-span-3'>
                        The scoreboard
                    </span>
                    <MaskLines
                        as='h2'
                        className='font-heading t-h2 font-semibold uppercase wdth-75 lg:col-span-9'
                        lines={['Existing payment fraud', 'solutions decline as many', 'as 20% of transactions.']}
                    />
                </div>

                <ol className='border-b border-tone-line'>
                    {SCORES.map((score, index) => (
                        <ScoreRow key={score.label} index={index} {...score} />
                    ))}
                </ol>
            </ToneSection>

            <ToneSection tone='gaming' id='area-of-effect' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
                <div className='grid gap-12 lg:grid-cols-12'>
                    <div className='flex flex-col gap-8 lg:col-span-5'>
                        <MaskLines
                            as='h2'
                            className='font-heading t-display font-semibold uppercase wdth-75'
                            lines={['A devastating', <span key='aoe' className='text-tone-accent'>area of effect.</span>]}
                        />
                        <Reveal className='t-lead max-w-md text-tone-muted'>
                            The gaming industry has grown by over 1 billion to approximately 3.24 billion players. Worth $365B today, the market has grown 7% annually and is expected to be worth $482B by 2027.
                        </Reveal>
                        <div>
                            <Cta href='/contact' cursor='Start'>
                                Start now
                            </Cta>
                        </div>
                    </div>

                    <div className='flex flex-col gap-14 lg:col-span-6 lg:col-start-7'>
                        <div className='flex flex-col gap-3 border-t border-tone-line pt-6'>
                            <span className='t-label text-tone-muted'>
                                Players worldwide
                            </span>
                            <span className='font-heading text-[clamp(5.5rem,14vw,13rem)] font-semibold leading-[0.8] tracking-[-0.05em] wdth-62'>
                                <CountUp to={3.24} decimals={2} suffix='B' />
                            </span>
                            <span className='text-tone-muted'>
                                Up by over 1 billion players.
                            </span>
                        </div>

                        <div className='grid grid-cols-2 gap-8'>
                            <div className='flex flex-col gap-2 border-t border-tone-line pt-5'>
                                <span className='t-label text-tone-muted'>
                                    Market today
                                </span>
                                <span className='font-heading text-6xl font-semibold tracking-[-0.04em] wdth-75 md:text-7xl'>
                                    $<CountUp to={365} suffix='B' />
                                </span>
                            </div>
                            <div className='flex flex-col gap-2 border-t border-tone-line pt-5'>
                                <span className='t-label text-tone-muted'>
                                    Annual growth
                                </span>
                                <span className='font-heading text-6xl font-semibold tracking-[-0.04em] wdth-75 md:text-7xl'>
                                    <CountUp to={7} suffix='%' />
                                </span>
                            </div>
                        </div>

                        <div className='flex flex-col gap-3'>
                            <div className='flex justify-between t-label text-tone-muted'>
                                <span>
                                    Today · $365B
                                </span>
                                <span className='text-tone-accent'>
                                    2027 · $482B
                                </span>
                            </div>
                            <motion.div
                                className='relative h-3 bg-tone/10'
                                initial='hidden'
                                whileInView='visible'
                                viewport={{ once: true }}
                            >
                                <motion.span
                                    className='absolute inset-y-0 left-0 bg-tone'
                                    variants={{
                                        hidden: { width: '0%' },
                                        visible: { width: '75.7%', transition: { duration: 1.6, ease: EASE_OUT_EXPO } }
                                    }}
                                />
                                <motion.span
                                    className='absolute inset-y-0 bg-tone-accent'
                                    style={{ left: '75.7%' }}
                                    variants={{
                                        hidden: { width: '0%' },
                                        visible: { width: '24.3%', transition: { duration: 1.2, ease: EASE_OUT_EXPO, delay: 1.2 } }
                                    }}
                                />
                            </motion.div>
                        </div>
                    </div>
                </div>
            </ToneSection>

            <Testimonials items={gamingVoices} tone='paper' label='Gaming, in their words' />

            <ResourceDownload tone='void' {...resources.gaming} />

            <MustHaves tone='gaming' label='Built in' />
        </>
    )
}
